"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useVoucherDialog = useVoucherDialog;
var RequestClearShoppingCache = _interopRequireWildcard(require("@ori-events/request-clear-shopping-cache"));
var RequestShoppingContextEvent = _interopRequireWildcard(require("@ori-events/request-shopping-context"));
var _auth = require("@ori/auth");
var _checkoutUtils = require("@ori/checkout-utils");
var _environment = require("@ori/environment");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _staticApiClientReact = require("@ori/static-api-client-react");
var _react = require("react");
var _constants = require("../../constants");
var _logger = require("../../logger");
var _types = require("../../types");
var _utils = require("../../utils");
function _getRequireWildcardCache(e) {
  if ("function" != typeof WeakMap) return null;
  var r = new WeakMap(),
    t = new WeakMap();
  return (_getRequireWildcardCache = function (e) {
    return e ? t : r;
  })(e);
}
function _interopRequireWildcard(e, r) {
  if (!r && e && e.__esModule) return e;
  if (null === e || "object" != typeof e && "function" != typeof e) return {
    default: e
  };
  var t = _getRequireWildcardCache(r);
  if (t && t.has(e)) return t.get(e);
  var n = {
      __proto__: null
    },
    a = Object.defineProperty && Object.getOwnPropertyDescriptor;
  for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) {
    var i = a ? Object.getOwnPropertyDescriptor(e, u) : null;
    i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u];
  }
  return n.default = e, t && t.set(e, n), n;
}
const {
  logWarning
} = (0, _checkoutUtils.getLoggers)(_constants.APP_NAME);
function useVoucherDialog({
  customerId,
  isVouchersDialogOpen,
  onClose,
  sessionId
}) {
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const [error, setError] = (0, _react.useState)();
  const [isVoucherApplied, setIsVoucherApplied] = (0, _react.useState)(false);
  const timerRef = (0, _react.useRef)();
  const apiClient = (0, _globalApiClientReact.useApiClient)();
  const staticApiClient = (0, _staticApiClientReact.useApiClient)();
  const handleDialogClose = (0, _react.useCallback)(() => {
    clearTimeout(timerRef.current);
    timerRef.current = undefined;
    onClose();
    setIsVoucherApplied(false);
    setError('');
  }, [onClose, setIsVoucherApplied]);
  const voucherApplied = (0, _react.useCallback)(code => {
    setIsVoucherApplied(true);
    setError('');
    if (isVouchersDialogOpen) {
      timerRef.current = setTimeout(handleDialogClose, _constants.DIALOG_CLOSE_TIMEOUT);
    }
    // analytics - voucher was successfully applied
    // TODO: there should be 3 additional params - voucher or referral code, voucher promotion, voucher code
    (0, _utils.dispatchUserActionEvent)([_types.MetaHierarchy.VoucherApplied, code]);
  }, [handleDialogClose, isVouchersDialogOpen, setIsVoucherApplied]);
  const addVoucherCode = (0, _react.useCallback)(async voucherCode => {
    try {
      await (0, _utils.addVoucher)(voucherCode);
      voucherApplied(voucherCode);
      return true;
    } catch (actionError) {
      if (actionError instanceof Error) {
        _logger.mainLogger.error(actionError, {
          action: 'addVoucherCode'
        });
      } else if (actionError !== '') {
        const newError = new Error(String(actionError));
        setError(String(actionError));
        _logger.mainLogger.error(newError, {
          action: 'addVoucherCode'
        });
      }
      return false;
    }
  }, [voucherApplied]);
  const handleSubmit = (0, _react.useCallback)(async event => {
    event.preventDefault();
    if (timerRef.current) {
      return;
    }
    const voucherCodeInput = event.currentTarget.elements.namedItem(_constants.VOUCHER_CODE_INPUT_NAME);
    if (!voucherCodeInput) {
      logWarning('Cannot find voucherCode input element in vouchers dialog form.');
    }
    const promoCode = voucherCodeInput.value;
    const {
      referral,
      voucher
    } = (0, _utils.splitPromoCode)(promoCode);
    if (customerId !== _auth.NOT_LOGGED_IN_USER_ID) {
      await addVoucherCode(voucher ?? promoCode);
      return;
    }
    try {
      const referralResponse = await (0, _staticApiClientReact.getTenantApplicationsShareAndEarnPersonalCodeCustomer)(staticApiClient, tenant, referral);
      try {
        await (0, _globalApiClientReact.createShoppingSessionReferral)(apiClient, sessionId, {
          customerId: referralResponse.data.customerId,
          referralType: _globalApiClientReact.GlobalShoppingReferralDtoReferralType.Pbs
        });
        (0, _checkoutUtils.setReferralInfo)(tenant, referral);
        if (voucher) {
          await (0, _utils.addVoucher)(voucher);
        }
        voucherApplied(promoCode);
        const clearCache = new CustomEvent(RequestClearShoppingCache.Name);
        window.dispatchEvent(clearCache);
        // setTimeout is set so the shopping context is requested after caches are cleared
        setTimeout(() => {
          const eventToTrigger = new CustomEvent(RequestShoppingContextEvent.Name);
          window.dispatchEvent(eventToTrigger);
        }, _constants.DIALOG_CLOSE_TIMEOUT);
      } catch (postReferralError) {
        const apiError = postReferralError;
        if (apiError.response?.data.Message) {
          setError(apiError.response.data.Message);
        }
        if (postReferralError instanceof Error) {
          _logger.mainLogger.error(postReferralError, {
            action: 'handleSubmit'
          });
        } else if (postReferralError !== '') {
          const newError = new Error(String(postReferralError));
          setError(String(postReferralError));
          _logger.mainLogger.error(newError, {
            action: 'handleSubmit'
          });
        }
      }
    } catch (referralError) {
      const isReferralVoucherAdded = await addVoucherCode(referral);
      if (isReferralVoucherAdded && voucher) {
        await addVoucherCode(voucher);
      }
    }
  }, [addVoucherCode, apiClient, customerId, sessionId, staticApiClient, tenant, voucherApplied]);
  return {
    handleDialogClose,
    handleSubmit,
    error,
    isVoucherApplied
  };
}